import { useState, useEffect, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const fetchingRole = useRef(false);
  const roleCache = useRef<Map<string, string>>(new Map());

  const fetchUserRole = useCallback(async (userId: string) => {
    // Prevent duplicate fetches
    if (fetchingRole.current) return;
    
    // Check cache first
    if (roleCache.current.has(userId)) {
      setUserRole(roleCache.current.get(userId)!);
      return;
    }

    fetchingRole.current = true;
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .single();

      if (!error && data) {
        const role = (data as any).role as string;
        roleCache.current.set(userId, role);
        setUserRole(role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    } finally {
      fetchingRole.current = false;
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Fetch user role when session changes
        if (session?.user) {
          fetchUserRole(session.user.id);
        } else {
          setUserRole(null);
          roleCache.current.clear();
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Fetch user role for existing session
      if (session?.user) {
        fetchUserRole(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserRole]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRole(null);
    roleCache.current.clear();
  };

  const isAdmin = userRole === 'admin';
  const isBreeder = userRole === 'breeder';
  const isUser = userRole === 'user';

  return { 
    user, 
    session, 
    loading, 
    signOut, 
    userRole,
    isAdmin,
    isBreeder,
    isUser
  };
};
